import { useState, useEffect, useCallback } from 'react';
import { BookMarked, RefreshCcw, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export const GlossaryLinkCard = ({ sessionId }) => {
  const [status, setStatus] = useState(null);
  const [glossaries, setGlossaries] = useState([]);
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const [rs, rg] = await Promise.all([
        fetch(`${API}/sessions/${sessionId}/glossary-status`, { headers: authHeaders() }),
        fetch(`${API}/glossaries`, { headers: authHeaders() }),
      ]);
      const ds = await rs.json().catch(() => ({}));
      const dg = await rg.json().catch(() => []);
      if (rs.ok) setStatus(ds);
      if (rg.ok) setGlossaries(dg);
    } catch { /* noop */ }
  }, [sessionId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (fn) => {
    setBusy(true); setErr('');
    try { await fn(); await load(); } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const link = () => act(async () => {
    const r = await fetch(`${API}/sessions/${sessionId}/glossary-link`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ glossary_id: pick || null }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.detail || `Erro ${r.status}`);
  });

  const applyUpdate = () => {
    if (!window.confirm('Aplicar a nova versão do glossário? Só afeta segmentos futuros; o snapshot anterior fica preservado no histórico.')) return;
    act(async () => {
      const r = await fetch(`${API}/sessions/${sessionId}/glossary/apply-update`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ confirm: true }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `Erro ${r.status}`);
    });
  };

  const snap = status?.snapshot;
  return (
    <div className="rounded-lg border border-white/10 bg-[#131B33]/50 p-4 space-y-2" data-testid="glossary-link-card">
      <div className="flex items-center gap-2 text-sm font-['Manrope'] font-bold text-white">
        <BookMarked className="w-4 h-4 text-amber-400" /> Glossário
        <a href="/glossaries" target="_blank" rel="noreferrer" className="ml-auto text-[10px] text-amber-400 hover:underline" data-testid="glossary-manage-link">Gerir</a>
      </div>
      {snap ? (
        <div className="text-[11px] space-y-1" data-testid="glossary-active-info">
          <div className="text-slate-300 font-semibold">{snap.name} <span className="text-slate-500 font-normal">· snapshot v{snap.version} · {snap.terms_count} termos ativos+aprovados</span></div>
          {snap.update_available && (
            <div className="flex items-center gap-2 rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-1.5" data-testid="glossary-update-available">
              <span className="text-sky-300">Nova versão v{snap.current_version} disponível.</span>
              <button onClick={applyUpdate} disabled={busy || !status?.can_change} data-testid="glossary-apply-update-btn"
                className="ml-auto inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-md border border-sky-500/50 text-sky-300 hover:bg-sky-500/15 disabled:opacity-40">
                <RefreshCcw className="w-3 h-3" /> Aplicar (em pausa)
              </button>
            </div>
          )}
          {!snap.exists && <div className="text-amber-400">Glossário original eliminado — o snapshot aplicado mantém-se.</div>}
        </div>
      ) : (
        <p className="text-[11px] text-slate-500" data-testid="glossary-none">Sem glossário associado.</p>
      )}
      <div className="flex items-center gap-2">
        <select value={pick} onChange={(e) => setPick(e.target.value)} data-testid="glossary-link-select"
          className="flex-1 bg-slate-950 border border-slate-700 rounded-md px-2 py-1.5 text-[11px] text-slate-200 focus:outline-none">
          <option value="">— remover / nenhum —</option>
          {glossaries.map((g) => (
            <option key={g.id} value={g.id}>{g.name} (v{g.version}, {g.approved_terms} aprov.)</option>
          ))}
        </select>
        <button onClick={link} disabled={busy || !status?.can_change} data-testid="glossary-link-btn"
          className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1.5 rounded-md border border-amber-600/50 text-amber-300 hover:bg-amber-600/15 disabled:opacity-40">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Associar
        </button>
      </div>
      {!status?.can_change && <p className="text-[9px] text-slate-600">Pause a sessão para associar ou atualizar o glossário.</p>}
      {err && <p className="text-[10px] text-red-400" data-testid="glossary-link-error">{err}</p>}
    </div>
  );
};
